import { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { sb } from '../lib/supabase';
import { fmt } from '../lib/helpers';
import {
  completeGateConditionTask,
  routeGateCondition,
  reopenGateCondition,
  fetchOpenGateConditionTasks,
} from '../lib/tasks';
import {
  GATE_STATUS_LABEL as STATUS_LABEL,
  PASSED_GATE_STATUSES as PASSED_STATUSES,
  scoreColor,
  worstRag,
  DELIVERABLE_ROLE_LABEL,
  gateExitStats,
} from '../lib/gateHelpers';
import {
  partitionPhaseDeliverables,
  buildSeedRows,
  fetchDeliverableTemplates,
  exitCriteriaOnly,
} from '../lib/gateDeliverables';
import { findNextLifecycleGate, shouldPropagateHandover } from '../lib/gateHandover';
import { fetchBakeOffGateStatus } from '../lib/solutionOptions';
import {
  seedBakeOffCriteria,
  syncBakeOffCriteriaMet,
  isDefineGate,
} from '../lib/gateBakeOff';
import { useSupabaseAuth } from '../context/AuthContext';
import useWorkspaceAccess from '../hooks/useWorkspaceAccess';
import Loader from '../components/Loader';
import Modal from '../components/Modal';
import SlideOver from '../components/SlideOver';
import Tabs from '../components/Tabs';
import TypeBadge from '../components/TypeBadge';
import OwnerSearch from '../components/OwnerSearch';

const GATE_TYPE_LABEL = { lifecycle: 'Lifecycle gate', release: 'Release gate', go_live: 'Go-live gate' };
const RAG_VALUES = ['red', 'amber', 'green'];

export default function StageGates() {
  const { programme, projects, project } = useOutletContext();
  const { profile } = useSupabaseAuth();
  const { canWrite } = useWorkspaceAccess();
  const singleProjectScope = Boolean(project || projects?.length === 1);

  const [gates, setGates] = useState([]);
  const [criteria, setCriteria] = useState([]);
  const [groups, setGroups] = useState([]);
  const [phaseDeliverables, setPhaseDeliverables] = useState([]);
  const [deps, setDeps] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [raidLinks, setRaidLinks] = useState([]);
  const [replans, setReplans] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openOnly, setOpenOnly] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [gateTab, setGateTab] = useState('overview');
  const [remarks, setRemarks] = useState([]);
  const [audit, setAudit] = useState([]);
  const [conditionTasks, setConditionTasks] = useState([]);
  const [bakeOffStatus, setBakeOffStatus] = useState(null);

  const [modal, setModal] = useState(null); // {kind, ...}
  const [searchParams] = useSearchParams();
  const focusConditionId = searchParams.get('focusCondition');

  const projectIds = useMemo(() => (projects || []).map((p) => p.id), [projects]);
  const projectName = useCallback((pid) => projects.find((p) => p.id === pid)?.name || 'Project', [projects]);

  const load = useCallback(async () => {
    if (!projectIds.length) { setGates([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data: gateRows } = await sb
        .from('stage_gates')
        .select('*')
        .in('project_id', projectIds)
        .order('order_index', { ascending: true });
      const gs = gateRows || [];
      setGates(gs);
      const gids = gs.map((g) => g.id);
      if (!gids.length) {
        setCriteria([]); setGroups([]); setPhaseDeliverables([]); setDeps([]); setMeetings([]); setOutcomes([]); setConditions([]); setRaidLinks([]); setReplans([]); setConditionTasks([]);
        return;
      }
      const [cRes, grRes, pdRes, dRes, mRes, oRes, condRes, rlRes, rpRes, taskRows] = await Promise.all([
        sb.from('gate_criteria').select('*, owner:profiles!gate_criteria_owner_id_fkey(full_name)').in('gate_id', gids).order('order_index'),
        sb.from('gate_criteria_groups').select('*').in('gate_id', gids).order('order_index'),
        sb.from('gate_phase_deliverables').select('*, deliverables(id, title, percentage_complete)').in('gate_id', gids).order('order_index'),
        sb.from('gate_dependencies').select('*').in('gate_id', gids),
        sb.from('gate_meetings').select('*, chair:profiles!gate_meetings_chair_id_fkey(full_name)').in('gate_id', gids).order('scheduled_at'),
        sb.from('gate_outcomes').select('*, recorder:profiles!gate_outcomes_recorded_by_fkey(full_name)').in('gate_id', gids),
        sb.from('gate_conditions').select('*, owner:profiles!gate_conditions_owner_id_fkey(full_name)').in('gate_id', gids),
        sb.from('gate_raid_links').select('id, gate_id, criterion_id, raid_item_id, raid_items(type, title, status, priority, profiles(full_name))').in('gate_id', gids),
        sb.from('replan_tasks').select('*').in('gate_id', gids),
        fetchOpenGateConditionTasks(projectIds),
      ]);
      setCriteria(cRes.data || []);
      setGroups(grRes.data || []);
      setPhaseDeliverables(pdRes.data || []);
      setDeps(dRes.data || []);
      setMeetings(mRes.data || []);
      setOutcomes(oRes.data || []);
      setConditions(condRes.data || []);
      setRaidLinks(rlRes.data || []);
      setReplans(rpRes.data || []);
      setConditionTasks(taskRows || []);

      const pid = project?.id || projectIds[0];
      const orgId = profile?.organisation_id;
      if (pid && programme?.id && orgId && gs.length) {
        try {
          let allCriteria = cRes.data || [];
          for (const gate of gs.filter((g) => isDefineGate(g))) {
            const gateCriteria = allCriteria.filter((c) => c.gate_id === gate.id);
            const inserted = await seedBakeOffCriteria(gate, orgId, gateCriteria);
            if (inserted.length) allCriteria = [...allCriteria, ...inserted];
          }
          await syncBakeOffCriteriaMet(pid, programme.id, allCriteria);
          const { data: refreshed } = await sb
            .from('gate_criteria')
            .select('*, owner:profiles!gate_criteria_owner_id_fkey(full_name)')
            .in('gate_id', gids)
            .order('order_index');
          if (refreshed) setCriteria(refreshed);
        } catch (bakeErr) {
          console.error('[StageGates] bake-off criteria setup failed', bakeErr);
        }
      }
    } catch (e) {
      console.error('[StageGates] load failed', e);
    } finally {
      setLoading(false);
    }
  }, [projectIds, project?.id, programme?.id, profile?.organisation_id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const pid = project?.id || projectIds[0];
    if (!pid || !programme?.id) { setBakeOffStatus(null); return; }
    fetchBakeOffGateStatus(pid, programme.id).then(setBakeOffStatus).catch(() => setBakeOffStatus(null));
  }, [project?.id, projectIds, programme?.id]);

  useEffect(() => {
    if (!focusConditionId || !conditions.length) return;
    const cond = conditions.find((c) => c.id === focusConditionId);
    if (cond) {
      setSelectedId(cond.gate_id);
      setGateTab('outcome');
    }
  }, [focusConditionId, conditions]);

  const conditionTaskById = useMemo(() => {
    const map = new Map();
    for (const t of conditionTasks) {
      map.set(t.source_artefact_id, t);
    }
    return map;
  }, [conditionTasks]);

  // Lazy-load remarks + audit when a gate opens
  useEffect(() => {
    if (!selectedId) { setRemarks([]); setAudit([]); return; }
    let cancelled = false;
    (async () => {
      const [rRes, aRes] = await Promise.all([
        sb.from('gate_remarks').select('*, author:profiles!gate_remarks_author_id_fkey(full_name)').eq('gate_id', selectedId).order('created_at', { ascending: false }),
        sb.from('gate_audit').select('*, actor:profiles!gate_audit_actor_id_fkey(full_name)').eq('gate_id', selectedId).order('created_at', { ascending: false }),
      ]);
      if (cancelled) return;
      setRemarks(rRes.data || []);
      setAudit(aRes.data || []);
    })();
    return () => { cancelled = true; };
  }, [selectedId]);

  const logAudit = useCallback(async (gateId, eventType, detail) => {
    const { data } = await sb.from('gate_audit')
      .insert({ gate_id: gateId, event_type: eventType, detail, actor_id: profile?.id || null })
      .select('*, actor:profiles!gate_audit_actor_id_fkey(full_name)');
    if (data?.[0] && gateId === selectedId) setAudit((a) => [data[0], ...a]);
  }, [profile?.id, selectedId]);

  async function refreshGate(gateId) {
    const { data } = await sb.from('stage_gates').select('*').eq('id', gateId).maybeSingle();
    if (data) setGates((gs) => gs.map((g) => (g.id === gateId ? data : g)));
  }

  // ── per-gate derived stats ──
  const gateStats = useCallback((gateId) => {
    const cs = exitCriteriaOnly(criteria.filter((c) => c.gate_id === gateId));
    const { met, total, pct } = gateExitStats(cs);
    const links = raidLinks.filter((l) => l.gate_id === gateId);
    const openRaid = links.filter((l) => l.raid_items && l.raid_items.status !== 'closed').length;
    const openConds = conditions.filter((c) => c.gate_id === gateId && c.status === 'open').length;
    const remarkable = cs.filter((c) => !c.is_met && (c.rag_forecast === 'red' || c.rag_forecast === 'amber' || c.rag_current === 'red'));
    return { cs, met, total, pct, openRaid, openConds, attention: remarkable.length };
  }, [criteria, raidLinks, conditions]);

  function depsSatisfied(gateId) {
    const gateDeps = deps.filter((d) => d.gate_id === gateId);
    const unmet = gateDeps.filter((d) => {
      const dep = gates.find((g) => g.id === d.depends_on_id);
      return dep && !PASSED_STATUSES.includes(dep.status);
    });
    return { total: gateDeps.length, unmet };
  }

  // ── summary cards ──
  const summary = useMemo(() => {
    const passed = gates.filter((g) => PASSED_STATUSES.includes(g.status)).length;
    const inProgress = gates.filter((g) => ['in_progress', 'ready_for_review'].includes(g.status)).length;
    const openConds = conditions.filter((c) => c.status === 'open').length;
    const openRaid = new Set(raidLinks.filter((l) => l.raid_items && l.raid_items.status !== 'closed').map((l) => l.raid_item_id)).size;
    return { passed, total: gates.length, inProgress, openConds, openRaid };
  }, [gates, conditions, raidLinks]);

  const visibleGates = openOnly ? gates.filter((g) => !PASSED_STATUSES.includes(g.status) && g.status !== 'failed') : gates;
  const lifecycleGates = visibleGates.filter((g) => g.gate_type === 'lifecycle');
  const childGates = visibleGates.filter((g) => g.gate_type !== 'lifecycle');

  const selected = selectedId ? gates.find((g) => g.id === selectedId) : null;

  if (loading) return <div className="content"><Loader text="Loading stage gates…" /></div>;

  return (
    <div className="content">
      {/* Summary */}
      <div className="m4">
        <div className="met"><div className="mlbl">Gates passed</div><div className="mval">{summary.passed}<span style={{ fontSize: 13, color: '#888780' }}> / {summary.total}</span></div><div className="msub">of total</div></div>
        <div className="met"><div className="mlbl">In progress</div><div className="mval">{summary.inProgress}</div><div className="msub">active review</div></div>
        <div className="met"><div className="mlbl">Open conditions</div><div className="mval" style={{ color: summary.openConds ? '#854f0b' : undefined }}>{summary.openConds}</div><div className="msub">to close</div></div>
        <div className="met"><div className="mlbl">Open RAID on gates</div><div className="mval" style={{ color: summary.openRaid ? '#A32D2D' : undefined }}>{summary.openRaid}</div><div className="msub">linked items</div></div>
      </div>

      {bakeOffStatus && project && (
        <div className="card" style={{ padding: 12, marginBottom: 16 }}>
          <div className="chdr"><span className="ctitle">Define gate — solutioning readiness</span></div>
          <ul className="bo-checklist">
            <li className={bakeOffStatus.baselineLocked ? 'met' : 'open'}>Requirements baseline locked</li>
            <li className={bakeOffStatus.optionsAssessed ? 'met' : 'open'}>Solution options assessed</li>
            <li className={bakeOffStatus.scenarioDecision ? 'met' : 'open'}>Scenario decision recorded</li>
            <li className={bakeOffStatus.canonicalArchitecture && bakeOffStatus.hasBusinessCase ? 'met' : 'open'}>Canonical architecture &amp; business case</li>
          </ul>
        </div>
      )}

      {/* Pipeline */}
      <div className="pipeline-wrap" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <span className="pipeline-title" style={{ marginBottom: 0 }}>Gate pipeline</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className={`ftab${openOnly ? ' active' : ''}`} onClick={() => setOpenOnly((v) => !v)}>Open only</button>
            {canWrite && <button className="btn-primary" onClick={() => setModal({ kind: 'gate', mode: 'add' })}>+ New gate</button>}
          </div>
        </div>
        {gates.length === 0 ? (
          <div className="empty">No gates yet. Create the first lifecycle gate to start the pipeline.</div>
        ) : (
          <>
            <div className="pipeline">
              {lifecycleGates.map((g, i) => {
                const prev = lifecycleGates[i - 1];
                return (
                  <div key={g.id} style={{ display: 'flex', alignItems: 'flex-start' }}>
                    {i > 0 && (
                      <div className="gate-connector"><div className={`connector-line${prev && PASSED_STATUSES.includes(prev.status) ? ' passed' : ''}`} /></div>
                    )}
                    <GateCard g={g} stats={gateStats(g.id)} active={selectedId === g.id} projectName={projectName} showProjectLabel={!singleProjectScope} onClick={() => { setSelectedId(g.id); setGateTab('overview'); }} />
                  </div>
                );
              })}
            </div>
            {childGates.length > 0 && (
              <>
                <div className="pipeline-title" style={{ marginTop: 18 }}>Release &amp; go-live gates</div>
                <div className="pipeline" style={{ gap: 12 }}>
                  {childGates.map((g) => (
                    <GateCard key={g.id} g={g} child stats={gateStats(g.id)} active={selectedId === g.id} projectName={projectName} showProjectLabel={!singleProjectScope} onClick={() => { setSelectedId(g.id); setGateTab('overview'); }} />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Detail panel */}
      {selected && (
        <GateDetail
          gate={selected}
          tab={gateTab} setTab={setGateTab}
          stats={gateStats(selected.id)}
          depStatus={depsSatisfied(selected.id)}
          criteria={criteria.filter((c) => c.gate_id === selected.id)}
          groups={groups.filter((gr) => gr.gate_id === selected.id)}
          deps={deps.filter((d) => d.gate_id === selected.id)}
          gates={gates}
          meetings={meetings.filter((m) => m.gate_id === selected.id)}
          outcomes={outcomes.filter((o) => o.gate_id === selected.id)}
          conditions={conditions.filter((c) => c.gate_id === selected.id)}
          raidLinks={raidLinks.filter((l) => l.gate_id === selected.id)}
          replans={replans.filter((r) => r.gate_id === selected.id)}
          remarks={remarks}
          audit={audit}
          profile={profile}
          canWrite={canWrite}
          projectName={projectName}
          programme={programme}
          phaseDeliverables={phaseDeliverables.filter((d) => d.gate_id === selected.id)}
          setPhaseDeliverables={setPhaseDeliverables}
          showProjectLabel={!singleProjectScope}
          onClose={() => setSelectedId(null)}
          setModal={setModal}
          sb={sb}
          reload={load}
          refreshGate={refreshGate}
          setCriteria={setCriteria}
          setRemarks={setRemarks}
          logAudit={logAudit}
          conditionTaskById={conditionTaskById}
          focusConditionId={focusConditionId}
        />
      )}

      {/* Modals */}
      {modal?.kind === 'gate' && (
        <GateModal modal={modal} gates={gates} projects={projects} defaultProjectId={project?.id || projects?.[0]?.id} profile={profile} onClose={() => setModal(null)} onSaved={async (gateId, isNew) => { setModal(null); await load(); if (isNew && gateId) logAudit(gateId, 'gate_created', {}); }} />
      )}
      {modal?.kind === 'group' && (
        <GroupModal gateId={modal.gateId} profile={profile} onClose={() => setModal(null)} onSaved={async () => { setModal(null); await load(); }} />
      )}
      {modal?.kind === 'criterion' && (
        <CriterionModal gateId={modal.gateId} groupId={modal.groupId} groups={groups.filter((g) => g.gate_id === modal.gateId)} onClose={() => setModal(null)} onSaved={async () => { setModal(null); await load(); }} />
      )}
      {modal?.kind === 'meeting' && (
        <MeetingModal gateId={modal.gateId} profile={profile} onClose={() => setModal(null)} onSaved={async () => { setModal(null); setGateTab('meetings'); await load(); }} />
      )}
      {modal?.kind === 'outcome' && (
        <OutcomeModal gateId={modal.gateId} meetingId={modal.meetingId} gate={gates.find((g) => g.id === modal.gateId)} gates={gates} conditions={conditions.filter((c) => c.gate_id === modal.gateId)} profile={profile} onClose={() => setModal(null)} onSaved={async (outcome) => { setModal(null); setGateTab('outcome'); await load(); logAudit(modal.gateId, 'outcome_recorded', { outcome }); }} />
      )}
      {modal?.kind === 'condition' && (
        <ConditionModal outcomeId={modal.outcomeId} gateId={modal.gateId} onClose={() => setModal(null)} onSaved={async () => { setModal(null); await load(); }} />
      )}
      {modal?.kind === 'dependency' && (
        <DependencyPicker gate={gates.find((g) => g.id === modal.gateId)} gates={gates} existing={deps.filter((d) => d.gate_id === modal.gateId)} profile={profile} onClose={() => setModal(null)} onSaved={async () => { setModal(null); await load(); }} />
      )}
      {modal?.kind === 'raid' && (
        <RaidLinkModal
          gateId={modal.gateId} criterionId={modal.criterionId} mode={modal.mode}
          programme={programme} gateProjectId={gates.find((g) => g.id === modal.gateId)?.project_id}
          existingLinks={raidLinks} profile={profile}
          onClose={() => setModal(null)}
          onSaved={async (raidTitle) => { setModal(null); await load(); logAudit(modal.gateId, modal.mode === 'raise' ? 'raid_raised' : 'raid_linked', { title: raidTitle }); }}
        />
      )}
      {modal?.kind === 'phase_deliverable' && (
        <PhaseDeliverableModal
          gateId={modal.gateId}
          item={modal.item}
          projectId={gates.find((g) => g.id === modal.gateId)?.project_id}
          onClose={() => setModal(null)}
          onSaved={async () => { setModal(null); await load(); }}
        />
      )}
    </div>
  );
}

// ── Pipeline card ────────────────────────────────────────────────────────────
function GateCard({ g, stats, active, child, projectName, showProjectLabel, onClick }) {
  return (
    <div className={`gate-card status-${g.status}${active ? ' active' : ''}${child ? ' child' : ''}`} onClick={onClick}>
      <div className="gate-card-type">{GATE_TYPE_LABEL[g.gate_type] || g.gate_type}</div>
      <div className="gate-card-name">{g.name}</div>
      {showProjectLabel && <div className="gate-card-proj">{projectName(g.project_id)}</div>}
      <div style={{ fontSize: 10, color: '#888780', display: 'flex', justifyContent: 'space-between' }}>
        <span>{stats.met}/{stats.total} exit criteria</span>
        <span>{stats.pct}%</span>
      </div>
      <div className="gate-card-bar"><span style={{ width: `${stats.pct}%`, background: scoreColor(stats.pct, stats.total) }} /></div>
      <div className="gate-card-foot">
        <span>{g.target_date ? fmt(g.target_date) : 'No date'}</span>
        <span className={`b ${g.status === 'passed' ? 'gr' : g.status === 'failed' ? 'rd' : g.status === 'conditional_pass' ? 'am' : ''}`} style={{ fontSize: 9 }}>{STATUS_LABEL[g.status]}</span>
      </div>
      {(stats.openRaid > 0 || stats.openConds > 0) && (
        <div className="gate-pills">
          {stats.openRaid > 0 && <span className="gate-pill risk">{stats.openRaid} RAID</span>}
          {stats.openConds > 0 && <span className="gate-pill cond">{stats.openConds} cond.</span>}
        </div>
      )}
    </div>
  );
}

// ── Detail panel ───────────────────────────────────────────────────────────────
function GateDetail(props) {
  const { gate, tab, setTab, stats, depStatus, meetings, outcomes, audit, projectName, showProjectLabel, phaseDeliverables, onClose } = props;
  const outcome = outcomes[0] || null;
  const ring = scoreColor(stats.pct, stats.total);
  const deliverableCount = phaseDeliverables?.length || 0;
  const handoverCount = partitionPhaseDeliverables(phaseDeliverables).handover.length;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'deliverables', label: `Phase deliverables (${deliverableCount})` },
    { id: 'readiness', label: `Exit readiness (${stats.met}/${stats.total})` },
    { id: 'handover', label: `Handover (${handoverCount})` },
    { id: 'dependencies', label: `Dependencies${depStatus.unmet.length ? ` ⚠${depStatus.unmet.length}` : ''}` },
    { id: 'meetings', label: `Meetings (${meetings.length})` },
    { id: 'outcome', label: 'Outcome' },
    { id: 'audit', label: 'Audit' },
  ];

  return (
    <SlideOver
      onClose={onClose}
      header={
        <div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 3 }}>
            <span className="gate-card-type">{GATE_TYPE_LABEL[gate.gate_type]}</span>
            {showProjectLabel && <span className="b pu" style={{ fontSize: 10 }}>{projectName(gate.project_id)}</span>}
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#2c2c2a', marginBottom: 6 }}>{gate.name}</div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <div className="dp-score-ring" style={{ borderColor: ring, color: ring }}>{stats.pct}%</div>
            <div style={{ fontSize: 11, color: '#5f5e5a' }}>
              <div>{stats.met}/{stats.total} exit criteria met</div>
              {stats.openRaid > 0 && <div style={{ color: '#A32D2D' }}>⚠ {stats.openRaid} open RAID</div>}
            </div>
            <span className={`b ${gate.status === 'passed' ? 'gr' : gate.status === 'failed' ? 'rd' : gate.status === 'conditional_pass' ? 'am' : ''}`}>{STATUS_LABEL[gate.status]}</span>
          </div>
        </div>
      }
    >
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      <div style={{ paddingTop: 14 }}>
        {tab === 'overview' && <OverviewTab {...props} outcome={outcome} />}
        {tab === 'deliverables' && <DeliverablesTab {...props} />}
        {tab === 'readiness' && <ReadinessTab {...props} />}
        {tab === 'handover' && <HandoverTab {...props} />}
        {tab === 'dependencies' && <DependenciesTab {...props} />}
        {tab === 'meetings' && <MeetingsTab {...props} outcome={outcome} />}
        {tab === 'outcome' && <OutcomeTab {...props} outcome={outcome} />}
        {tab === 'audit' && <AuditTab audit={audit} />}
      </div>
    </SlideOver>
  );
}

function Pill({ label, value, color }) {
  return (
    <div style={{ flex: 1, padding: '10px 12px', border: '0.5px solid #f1efe8', borderRadius: 9, textAlign: 'center' }}>
      <div style={{ fontSize: 18, fontWeight: 600, color: color || '#2c2c2a' }}>{value}</div>
      <div style={{ fontSize: 10, color: '#888780' }}>{label}</div>
    </div>
  );
}

function OverviewTab({ gate, stats, criteria, groups, phaseDeliverables, setModal, canWrite }) {
  const remaining = stats.total - stats.met;
  const groupName = (id) => groups.find((g) => g.id === id)?.name || 'Ungrouped';
  const focus = criteria.filter((c) => !c.is_met && (c.rag_forecast === 'red' || c.rag_forecast === 'amber' || c.rag_current === 'red'));
  const onTrack = criteria.filter((c) => !c.is_met && c.rag_forecast === 'green');
  return (
    <div>
      <div className="gate-banner info" style={{ marginBottom: 12 }}>
        <span>Phase deliverables ({phaseDeliverables?.length || 0}) track what this phase must produce. Exit readiness ({stats.met}/{stats.total}) is the formal gate scorecard.</span>
      </div>
      {gate.status === 'ready_for_review' && (
        <div className="gate-banner info">
          <span>✓ All criteria met — this gate is ready for a formal review.</span>
          {canWrite && <button className="row-act" onClick={() => setModal({ kind: 'meeting', gateId: gate.id })}>Schedule review</button>}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Pill label="Met" value={stats.met} color="#1D9E75" />
        <Pill label="Remaining" value={remaining} />
        <Pill label="Need attention" value={stats.attention} color={stats.attention ? '#854f0b' : undefined} />
        <Pill label="Open RAID" value={stats.openRaid} color={stats.openRaid ? '#A32D2D' : undefined} />
      </div>
      <div className="ctitle" style={{ marginBottom: 8 }}>Dry-run focus list</div>
      {focus.length === 0 ? <div style={{ fontSize: 11, color: '#888780', marginBottom: 16 }}>Nothing at risk — no unmet criteria are red or amber.</div> : (
        <div style={{ marginBottom: 16 }}>
          {focus.map((c) => (
            <div key={c.id} className="focus-row">
              <RagInline value={c.rag_current} /> <span style={{ color: '#888780' }}>→</span> <RagInline value={c.rag_forecast} />
              <span style={{ flex: 1, fontSize: 12, color: '#2c2c2a' }}>{c.name}</span>
              <span style={{ fontSize: 10, color: '#888780' }}>{groupName(c.group_id)}</span>
            </div>
          ))}
        </div>
      )}
      {onTrack.length > 0 && (
        <>
          <div className="ctitle" style={{ marginBottom: 8 }}>On track (forecast green)</div>
          {onTrack.map((c) => (
            <div key={c.id} className="focus-row">
              <RagInline value={c.rag_forecast} />
              <span style={{ flex: 1, fontSize: 12, color: '#2c2c2a' }}>{c.name}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function RagInline({ value }) {
  const colors = { red: '#E24B4A', amber: '#EF9F27', green: '#1D9E75' };
  return <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: colors[value] || '#d3d1c7', border: value ? 'none' : '1.5px dashed #d3d1c7', flexShrink: 0 }} />;
}

function DeliverablesTab({ gate, phaseDeliverables, programme, setModal, sb, setPhaseDeliverables, logAudit, reload, canWrite }) {
  const [seeding, setSeeding] = useState(false);
  const orgId = programme?.organisation_id;

  async function seedFromTemplates() {
    if (!orgId) return;
    setSeeding(true);
    try {
      const templates = await fetchDeliverableTemplates(sb, {
        organisationId: orgId,
        gateType: gate.gate_type,
        stageId: gate.stage_id,
      });
      const rows = buildSeedRows(templates, gate.id, phaseDeliverables);
      if (!rows.length) return;
      const { data, error } = await sb.from('gate_phase_deliverables').insert(rows).select('*, deliverables(id, title, percentage_complete)');
      if (error) throw error;
      setPhaseDeliverables((prev) => [...prev, ...(data || [])]);
      logAudit(gate.id, 'remark_added', { title: `Seeded ${data?.length || 0} deliverables from templates` });
      await reload();
    } catch (e) {
      console.error('[StageGates] seed deliverables failed', e);
    } finally {
      setSeeding(false);
    }
  }

  async function removeItem(item) {
    const { error } = await sb.from('gate_phase_deliverables').delete().eq('id', item.id);
    if (error) return;
    setPhaseDeliverables((prev) => prev.filter((d) => d.id !== item.id));
    logAudit(gate.id, 'remark_added', { title: `Removed phase deliverable: ${item.title}` });
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: '#5f5e5a' }}>Outputs this phase must produce. Handover outputs commit at gate pass.</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canWrite && (
            <>
              <button className="btn-ghost" type="button" disabled={seeding || !orgId} onClick={seedFromTemplates}>
                {seeding ? 'Seeding…' : 'Seed from templates'}
              </button>
              <button className="row-act" type="button" onClick={() => setModal({ kind: 'phase_deliverable', gateId: gate.id })}>+ Add deliverable</button>
            </>
          )}
        </div>
      </div>
      {phaseDeliverables.length === 0 ? (
        <div className="empty">No phase deliverables defined yet. Add what this gate phase must produce before exit review.</div>
      ) : (
        phaseDeliverables.map((d) => (
          <div key={d.id} className="focus-row" style={{ alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#2c2c2a' }}>{d.title}</div>
              {d.description && <div style={{ fontSize: 11, color: '#888780', marginTop: 2 }}>{d.description}</div>}
              {d.deliverables?.title && (
                <div style={{ fontSize: 10, color: '#534AB7', marginTop: 4 }}>Linked: {d.deliverables.title}{d.deliverables.percentage_complete != null ? ` (${d.deliverables.percentage_complete}%)` : ''}</div>
              )}
              <span className="b" style={{ fontSize: 9, marginTop: 6, display: 'inline-block' }}>{DELIVERABLE_ROLE_LABEL[d.role] || d.role}</span>
            </div>
            {canWrite && (
              <>
                <button className="row-act" onClick={() => setModal({ kind: 'phase_deliverable', gateId: gate.id, item: d })}>Edit</button>
                <button className="row-act" style={{ color: '#A32D2D' }} onClick={() => removeItem(d)}>Remove</button>
              </>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function HandoverTab({ gate, phaseDeliverables, gates, setModal, canWrite }) {
  const handover = partitionPhaseDeliverables(phaseDeliverables).handover;
  const passed = ['passed', 'conditional_pass'].includes(gate.status);
  const nextGate = findNextLifecycleGate(gates, gate);

  return (
    <div>
      <div className="gate-banner info" style={{ marginBottom: 12 }}>
        <span>
          Handover outputs commit when this gate passes (Pass or Conditional pass).
          {nextGate
            ? ` On pass, ${handover.length} item${handover.length !== 1 ? 's' : ''} propagate to inherited inputs on "${nextGate.name}".`
            : ' No subsequent lifecycle gate on this project — handover items stay on this gate only.'}
        </span>
      </div>
      {handover.length === 0 ? (
        <div className="empty">No handover outputs defined. Add deliverables with role &quot;Handover output&quot; or seed from templates.</div>
      ) : (
        handover.map((d) => (
          <div key={d.id} className="focus-row">
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 500 }}>{d.title}</div>
              {d.description && <div style={{ fontSize: 11, color: '#888780', marginTop: 2 }}>{d.description}</div>}
            </div>
            <span className={`b ${passed ? 'gr' : 'am'}`} style={{ fontSize: 9 }}>{passed ? 'Committed at pass' : 'Pending pass'}</span>
            {canWrite && <button className="row-act" type="button" onClick={() => setModal({ kind: 'phase_deliverable', gateId: gate.id, item: d })}>Edit</button>}
          </div>
        ))
      )}
    </div>
  );
}

function ReadinessTab({ gate, criteria, groups, raidLinks, profile, setModal, sb, refreshGate, setCriteria, logAudit, canWrite }) {
  const [activeGroup, setActiveGroup] = useState('all');
  const [ragPicker, setRagPicker] = useState(null); // {criterionId, field}
  const [flash, setFlash] = useState(null);

  const exitCriteria = exitCriteriaOnly(criteria);
  const groupTabs = [{ id: 'all', name: 'All' }, ...groups];
  const shown = activeGroup === 'all' ? exitCriteria : exitCriteria.filter((c) => c.group_id === activeGroup);

  function groupMeta(gid) {
    const cs = exitCriteria.filter((c) => c.group_id === gid);
    const met = cs.filter((c) => c.is_met).length;
    const atRisk = cs.filter((c) => !c.is_met && (c.rag_forecast === 'red' || c.rag_forecast === 'amber')).length;
    return { worst: worstRag(cs.map((c) => c.rag_forecast)), met, total: cs.length, atRisk };
  }

  async function toggleMet(c) {
    const next = !c.is_met;
    if (next && c.rag_forecast !== 'green') {
      setFlash(c.id);
      setTimeout(() => setFlash(null), 2200);
      return;
    }
    const patch = { is_met: next, met_at: next ? new Date().toISOString() : null, met_by: next ? profile?.id || null : null };
    const { error } = await sb.from('gate_criteria').update(patch).eq('id', c.id);
    if (error) { setFlash(c.id); setTimeout(() => setFlash(null), 2200); return; }
    setCriteria((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...patch } : x)));
    logAudit(c.gate_id, next ? 'criterion_met' : 'criterion_unmet', { name: c.name });
    refreshGate(c.gate_id);
  }

  async function setRag(c, field, value) {
    await sb.from('gate_criteria').update({ [field]: value }).eq('id', c.id);
    setCriteria((prev) => prev.map((x) => (x.id === c.id ? { ...x, [field]: value } : x)));
    logAudit(c.gate_id, 'rag_change', { name: c.name, field, value });
    setRagPicker(null);
  }

  return (
    <div>
      <div className="gate-group-tabs">
        {groupTabs.map((g) => {
          const m = g.id === 'all' ? null : groupMeta(g.id);
          return (
            <button key={g.id} className={`gate-group-tab${activeGroup === g.id ? ' active' : ''}`} onClick={() => setActiveGroup(g.id)}>
              {m && <RagInline value={m.worst} />}
              {g.name}
              {m && <span style={{ color: '#888780' }}>{m.met}/{m.total}{m.atRisk ? ` ⚠${m.atRisk}` : ''}</span>}
            </button>
          );
        })}
        {canWrite && <button className="gate-group-tab" onClick={() => setModal({ kind: 'group', gateId: gate.id })}>+ Group</button>}
      </div>

      {canWrite && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button className="row-act" onClick={() => setModal({ kind: 'criterion', gateId: gate.id, groupId: activeGroup === 'all' ? null : activeGroup })}>+ Add criterion</button>
        </div>
      )}

      {shown.length === 0 ? <div className="empty">No criteria in this group yet.</div> : shown.map((c) => {
        const atRiskClass = c.rag_forecast === 'red' ? ' rag-red-risk' : (c.rag_forecast === 'amber' || c.rag_current === 'red') ? ' rag-amber-risk' : '';
        const raidForCrit = raidLinks.filter((l) => l.criterion_id === c.id && l.raid_items);
        return (
          <div key={c.id} className={`crit-row${c.is_met ? ' met' : ''}${atRiskClass}`}>
            <div className="crit-main">
              {canWrite
                ? <input type="checkbox" className="crit-check" checked={!!c.is_met} onChange={() => toggleMet(c)} title={c.rag_forecast !== 'green' && !c.is_met ? 'Set forecast RAG to green before marking complete.' : ''} />
                : <span className="crit-check" style={{ opacity: c.is_met ? 1 : 0.35 }}>{c.is_met ? '✓' : '○'}</span>}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, color: '#2c2c2a', fontWeight: 500, textDecoration: c.is_met ? 'line-through' : 'none' }}>{c.name}</div>
                {c.description && <div style={{ fontSize: 11, color: '#888780', marginTop: 2 }}>{c.description}</div>}
                <div style={{ fontSize: 10, color: '#888780', marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {c.owner?.full_name && <span>Owner: {c.owner.full_name}</span>}
                  {c.is_met && c.met_at && <span>Met {fmt(c.met_at)}</span>}
                  {raidForCrit.length > 0 && <span style={{ color: '#A32D2D' }}>⚠ {raidForCrit.length} RAID</span>}
                </div>
                {flash === c.id && <div style={{ fontSize: 10.5, color: '#A32D2D', marginTop: 4 }}>Set forecast RAG to green before marking complete.</div>}
                {raidForCrit.map((l) => (
                  <div key={l.id} style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
                    <TypeBadge type={l.raid_items.type} short />
                    <span style={{ fontSize: 11, color: '#5f5e5a' }}>{l.raid_items.title}</span>
                  </div>
                ))}
                {canWrite && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 5 }}>
                    <button className="row-act" onClick={() => setModal({ kind: 'raid', gateId: c.gate_id, criterionId: c.id, mode: 'raise' })}>Raise RAID</button>
                    <button className="row-act" onClick={() => setModal({ kind: 'raid', gateId: c.gate_id, criterionId: c.id, mode: 'link' })}>Link RAID</button>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                <div style={{ fontSize: 9, color: '#888780' }}>now → gate</div>
                <div style={{ display: 'flex', gap: 6, position: 'relative' }}>
                  {canWrite ? (
                    <>
                      <RagButton value={c.rag_current} onClick={() => setRagPicker({ id: c.id, field: 'rag_current' })} />
                      <RagButton value={c.rag_forecast} onClick={() => setRagPicker({ id: c.id, field: 'rag_forecast' })} />
                      {ragPicker?.id === c.id && (
                        <div className="rag-pop" style={{ top: 22, right: 0 }} onMouseLeave={() => setRagPicker(null)}>
                          {RAG_VALUES.map((v) => <RagButton key={v} value={v} onClick={() => setRag(c, ragPicker.field, v)} />)}
                          <button className="rag-dot-btn none" title="clear" onClick={() => setRag(c, ragPicker.field, null)} />
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <RagInline value={c.rag_current} />
                      <RagInline value={c.rag_forecast} />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RagButton({ value, onClick }) {
  return <button type="button" className={`rag-dot-btn ${value || 'none'}`} onClick={onClick} title={value || 'not set'} />;
}

function DependenciesTab({ gate, deps, gates, depStatus, setModal, sb, reload, canWrite }) {
  async function remove(id) { await sb.from('gate_dependencies').delete().eq('id', id); reload(); }
  return (
    <div>
      {depStatus.unmet.length > 0 && (
        <div className="gate-banner warn" style={{ display: 'block' }}>⚠ {depStatus.unmet.length} dependency gate{depStatus.unmet.length > 1 ? 's are' : ' is'} not yet passed. This gate should not be formally reviewed until they pass.</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span className="ctitle">Depends on</span>
        {canWrite && <button className="row-act" onClick={() => setModal({ kind: 'dependency', gateId: gate.id })}>+ Add dependency</button>}
      </div>
      {deps.length === 0 ? <div className="empty">No dependencies configured.</div> : deps.map((d) => {
        const dep = gates.find((g) => g.id === d.depends_on_id);
        const ok = dep && PASSED_STATUSES.includes(dep.status);
        return (
          <div key={d.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', border: '0.5px solid #d3d1c7', borderRadius: 8, marginBottom: 5 }}>
            <div>
              <div style={{ fontSize: 12, color: '#2c2c2a', fontWeight: 500 }}>{dep?.name || 'Gate'}</div>
              <div style={{ fontSize: 10, color: ok ? '#1D9E75' : '#854f0b' }}>{ok ? '✓ satisfied' : `pending — ${STATUS_LABEL[dep?.status] || 'unknown'}`}</div>
            </div>
            {canWrite && <button className="row-act row-act-close" onClick={() => remove(d.id)}>✕</button>}
          </div>
        );
      })}
    </div>
  );
}

function MeetingsTab({ gate, meetings, outcomes, setModal, canWrite }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span className="ctitle">Meetings</span>
        {canWrite && <button className="row-act" onClick={() => setModal({ kind: 'meeting', gateId: gate.id })}>+ Schedule meeting</button>}
      </div>
      {meetings.length === 0 ? <div className="empty">No meetings scheduled. Dry runs surface issues; the formal review records the outcome.</div> : meetings.map((m) => {
        const hasOutcome = outcomes.some((o) => o.meeting_id === m.id);
        return (
          <div key={m.id} style={{ padding: '10px 12px', border: '0.5px solid #d3d1c7', borderRadius: 9, marginBottom: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className={`b ${m.meeting_type === 'formal' ? 'pu' : ''}`}>{m.meeting_type === 'formal' ? 'Formal review' : 'Dry run'}</span>
              <span style={{ fontSize: 11, color: '#888780' }}>{m.scheduled_at ? fmt(m.scheduled_at) : 'No date'}</span>
            </div>
            {m.title && <div style={{ fontSize: 12.5, color: '#2c2c2a', fontWeight: 500, marginTop: 5 }}>{m.title}</div>}
            {m.chair?.full_name && <div style={{ fontSize: 10.5, color: '#888780', marginTop: 2 }}>Chair: {m.chair.full_name}</div>}
            {m.notes && <div style={{ fontSize: 11, color: '#5f5e5a', marginTop: 4 }}>{m.notes}</div>}
            {canWrite && m.meeting_type === 'formal' && !hasOutcome && (
              <button className="row-act" style={{ marginTop: 6 }} onClick={() => setModal({ kind: 'outcome', gateId: gate.id, meetingId: m.id })}>Record outcome</button>
            )}
            {hasOutcome && <div style={{ fontSize: 10.5, color: '#1D9E75', marginTop: 6 }}>✓ Outcome recorded</div>}
          </div>
        );
      })}
    </div>
  );
}

function OutcomeTab({
  gate, outcome, meetings, conditions, replans, setModal, reload, canWrite, conditionTaskById, focusConditionId,
}) {
  const OUTCOME_LABEL = { pass: 'Pass', conditional_pass: 'Conditional pass', fail: 'Fail', defer: 'Defer' };
  const formalMeeting = meetings.find((m) => m.meeting_type === 'formal');
  const [conditionError, setConditionError] = useState(null);

  async function toggleCondition(c) {
    setConditionError(null);
    try {
      if (c.status === 'open') {
        const taskId = conditionTaskById?.get(c.id)?.id || await routeGateCondition(c.id);
        await completeGateConditionTask(taskId);
      } else {
        await reopenGateCondition(c.id);
      }
      await reload();
    } catch (e) {
      console.error('[StageGates] update condition failed', e);
      setConditionError(e.message || 'Could not update the gate condition.');
    }
  }

  if (!outcome) {
    if (!formalMeeting) {
      return <div className="gate-banner warn" style={{ display: 'block' }}>No formal review scheduled yet. Schedule a formal review on the Meetings tab, then record its outcome.</div>;
    }
    return (
      <div>
        <div style={{ fontSize: 12, color: '#5f5e5a', marginBottom: 12 }}>A formal review exists but no outcome has been recorded.</div>
        {canWrite && <button className="btn-primary" onClick={() => setModal({ kind: 'outcome', gateId: gate.id, meetingId: formalMeeting.id })}>Record outcome</button>}
      </div>
    );
  }

  const openConds = conditions.filter((c) => c.status === 'open');
  return (
    <div>
      <div className={`outcome-card ${outcome.outcome}`}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#2c2c2a' }}>{OUTCOME_LABEL[outcome.outcome]}</span>
          <span style={{ fontSize: 10, color: '#888780' }}>Immutable</span>
        </div>
        {outcome.rationale && <div style={{ fontSize: 12, color: '#5f5e5a', marginTop: 8, lineHeight: 1.55 }}>{outcome.rationale}</div>}
        <div style={{ fontSize: 10.5, color: '#888780', marginTop: 8 }}>Recorded by {outcome.recorder?.full_name || 'PM'} · {fmt(outcome.recorded_at)}</div>
      </div>

      {outcome.outcome === 'conditional_pass' && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span className="ctitle">Conditions {openConds.length === 0 && conditions.length > 0 ? '— all met ✓' : ''}</span>
            {canWrite && <button className="row-act" onClick={() => setModal({ kind: 'condition', gateId: gate.id, outcomeId: outcome.id })}>+ Add condition</button>}
          </div>
          {conditionError && <div className="form-error" style={{ marginBottom: 8 }}>{conditionError}</div>}
          {conditions.length === 0 ? <div style={{ fontSize: 11, color: '#888780' }}>No conditions recorded.</div> : conditions.map((c) => {
            const task = conditionTaskById?.get(c.id);
            const focused = c.id === focusConditionId;
            return (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '0.5px solid #f1efe8', borderRadius: 8, marginBottom: 5, ...(focused ? { boxShadow: '0 0 0 2px #534AB7', background: '#EEEDFE' } : {}) }}>
              {canWrite
                ? <input type="checkbox" className="crit-check" checked={c.status === 'met'} onChange={() => toggleCondition(c)} />
                : <span className="crit-check" style={{ opacity: c.status === 'met' ? 1 : 0.35 }}>{c.status === 'met' ? '✓' : '○'}</span>}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: '#2c2c2a', textDecoration: c.status === 'met' ? 'line-through' : 'none' }}>{c.description}</div>
                <div style={{ fontSize: 10, color: '#888780' }}>
                  {c.owner?.full_name && `${c.owner.full_name} · `}
                  {c.due_date ? `due ${fmt(c.due_date)}` : 'no due date'}
                  {task?.profiles?.full_name && c.status === 'open' && ` · task: ${task.profiles.full_name}`}
                </div>
              </div>
            </div>
            );
          })}
          {conditions.length > 0 && openConds.length === 0 && (
            <div className="gate-banner info" style={{ marginTop: 8 }}>All conditions met — gate will move to Passed.</div>
          )}
        </div>
      )}

      {(outcome.outcome === 'fail' || outcome.outcome === 'conditional_pass') && (
        <div className="gate-banner warn" style={{ marginTop: 16, display: 'block' }}>
          ↻ A replan task was triggered for this gate ({replans.length ? STATUS_LABEL[replans[0].status] || replans[0].status : 'open'}). Replanning is carried out in Management → Plan &amp; milestones.
        </div>
      )}
    </div>
  );
}

function AuditTab({ audit }) {
  const LABEL = {
    gate_created: 'Gate created', criterion_met: 'Criterion met', criterion_unmet: 'Criterion reopened',
    rag_change: 'RAG updated', raid_raised: 'RAID raised', raid_linked: 'RAID linked',
    remark_added: 'Remark added', outcome_recorded: 'Outcome recorded', close_date_changed: 'Close date changed',
  };
  if (!audit.length) return <div className="empty">No audit events yet.</div>;
  return (
    <div>
      {audit.map((a) => (
        <div key={a.id} style={{ padding: '8px 0', borderBottom: '0.5px solid #f1efe8' }}>
          <div style={{ fontSize: 12, color: '#2c2c2a' }}>{LABEL[a.event_type] || a.event_type}{a.detail?.name ? `: ${a.detail.name}` : a.detail?.title ? `: ${a.detail.title}` : a.detail?.outcome ? `: ${a.detail.outcome}` : a.detail?.value ? ` → ${a.detail.value}` : ''}</div>
          <div style={{ fontSize: 10, color: '#888780', marginTop: 2 }}>{a.actor?.full_name || 'System'} · {fmt(a.created_at)}</div>
        </div>
      ))}
    </div>
  );
}

// ── Modals ──────────────────────────────────────────────────────────────────────
function PhaseDeliverableModal({ gateId, item, projectId, onClose, onSaved }) {
  const editing = item || null;
  const [title, setTitle] = useState(editing?.title || '');
  const [description, setDescription] = useState(editing?.description || '');
  const [role, setRole] = useState(editing?.role || 'phase_output');
  const [deliverableId, setDeliverableId] = useState(editing?.deliverable_id || '');
  const [projectDeliverables, setProjectDeliverables] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      const { data } = await sb.from('deliverables').select('id, title, percentage_complete').eq('project_id', projectId).order('order_index');
      if (!cancelled) setProjectDeliverables(data || []);
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    if (!deliverableId) return;
    const linked = projectDeliverables.find((d) => d.id === deliverableId);
    if (linked && !title.trim()) setTitle(linked.title);
  }, [deliverableId, projectDeliverables, title]);

  async function save() {
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    const payload = {
      gate_id: gateId,
      title: title.trim(),
      description: description.trim() || null,
      role,
      deliverable_id: deliverableId || null,
    };
    const res = editing
      ? await sb.from('gate_phase_deliverables').update(payload).eq('id', editing.id)
      : await sb.from('gate_phase_deliverables').insert(payload);
    setSaving(false);
    if (res.error) {
      setError(res.error.message);
      return;
    }
    onSaved();
  }

  return (
    <Modal title={editing ? 'Edit phase deliverable' : 'Add phase deliverable'} onClose={onClose} busy={saving}
      footer={<><button className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button><button className="btn-primary" onClick={save} disabled={saving || !title.trim()}>{saving ? 'Saving…' : 'Save'}</button></>}>
      <div className="form-row"><label className="form-label">Title *</label><input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Detailed design pack approved" /></div>
      <div className="form-row"><label className="form-label">Description</label><textarea className="form-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} /></div>
      <div className="form-row"><label className="form-label">Role</label>
        <select className="form-select" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="inherited_input">Inherited input</option>
          <option value="phase_output">Phase output</option>
          <option value="handover_output">Handover output</option>
        </select>
      </div>
      {projectDeliverables.length > 0 && (
        <div className="form-row"><label className="form-label">Link to deliverable (optional)</label>
          <select className="form-select" value={deliverableId} onChange={(e) => setDeliverableId(e.target.value)}>
            <option value="">None — title only</option>
            {projectDeliverables.map((d) => (
              <option key={d.id} value={d.id}>{d.title}{d.percentage_complete != null ? ` (${d.percentage_complete}%)` : ''}</option>
            ))}
          </select>
        </div>
      )}
      {error && <div className="form-error" style={{ margin: 0 }}>{error}</div>}
    </Modal>
  );
}

function GateModal({ modal, gates, projects, defaultProjectId, profile, onClose, onSaved }) {
  const editing = modal.mode === 'edit' ? modal.gate : null;
  const singleProject = (projects || []).length === 1;
  const [name, setName] = useState(editing?.name || '');
  const [type, setType] = useState(editing?.gate_type || 'lifecycle');
  const [projectId, setProjectId] = useState(editing?.project_id || defaultProjectId || projects[0]?.id || '');
  const [target, setTarget] = useState(editing?.target_date || '');
  const [depIds, setDepIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    setSaving(true); setError(null);
    const payload = { name: name.trim(), gate_type: type, project_id: projectId, target_date: target || null };
    let res;
    if (editing) res = await sb.from('stage_gates').update(payload).eq('id', editing.id).select('id');
    else res = await sb.from('stage_gates').insert({ ...payload, status: 'not_started', created_by: profile?.id || null, order_index: gates.length + 1 }).select('id');
    if (res.error) { setSaving(false); setError(res.error.message); return; }
    const gateId = res.data?.[0]?.id;
    if (gateId && depIds.length) {
      await sb.from('gate_dependencies').insert(depIds.map((d) => ({ gate_id: gateId, depends_on_id: d, created_by: profile?.id || null })));
    }
    setSaving(false);
    onSaved(gateId, !editing);
  }

  return (
    <Modal title={editing ? 'Edit gate' : 'New gate'} onClose={onClose} busy={saving}
      footer={<><button className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button><button className="btn-primary" onClick={save} disabled={saving || !name.trim() || !projectId}>{saving ? 'Saving…' : editing ? 'Save' : 'Create gate'}</button></>}>
      <div className="form-row"><label className="form-label">Gate name *</label><input className="form-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. G3 Build complete" /></div>
      <div className="form-2col">
        <div className="form-row"><label className="form-label">Type</label>
          <select className="form-select" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="lifecycle">Lifecycle</option><option value="release">Release</option><option value="go_live">Go-live</option>
          </select>
        </div>
        <div className="form-row"><label className="form-label">Target date</label><input className="form-input" type="date" value={target || ''} onChange={(e) => setTarget(e.target.value)} /></div>
      </div>
      <div className="form-row"><label className="form-label">Project</label>
        {singleProject ? (
          <div className="form-input" style={{ background: '#f7f6f3', color: '#5f5e5a' }}>{projects[0]?.name || 'Current project'}</div>
        ) : (
          <select className="form-select" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            {(projects || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
      </div>
      {!editing && gates.length > 0 && (
        <div className="form-row">
          <label className="form-label">Depends on (must pass first)</label>
          <div style={{ maxHeight: 160, overflowY: 'auto', border: '0.5px solid #d3d1c7', borderRadius: 8, padding: 6 }}>
            {gates.map((g) => (
              <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px', fontSize: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={depIds.includes(g.id)} onChange={(e) => setDepIds((prev) => e.target.checked ? [...prev, g.id] : prev.filter((x) => x !== g.id))} />
                {g.name} <span style={{ color: '#888780', fontSize: 10 }}>{STATUS_LABEL[g.status]}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      {error && <div className="form-error" style={{ margin: 0 }}>{error}</div>}
    </Modal>
  );
}

function GroupModal({ gateId, onClose, onSaved }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    await sb.from('gate_criteria_groups').insert({ gate_id: gateId, name: name.trim() });
    setSaving(false); onSaved();
  }
  return (
    <Modal title="Add criteria group" onClose={onClose} busy={saving}
      footer={<><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={save} disabled={saving || !name.trim()}>Add group</button></>}>
      <div className="form-row"><label className="form-label">Group name *</label><input className="form-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Delivery, Operations, Governance" /></div>
    </Modal>
  );
}

function CriterionModal({ gateId, groupId, groups, onClose, onSaved }) {
  const [name, setName] = useState('');
  const [gid, setGid] = useState(groupId || groups[0]?.id || '');
  const [ownerId, setOwnerId] = useState(null);
  const [ownerName, setOwnerName] = useState('');
  const [desc, setDesc] = useState('');
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    await sb.from('gate_criteria').insert({ gate_id: gateId, group_id: gid || null, name: name.trim(), description: desc.trim() || null, owner_id: ownerId || null });
    setSaving(false); onSaved();
  }
  return (
    <Modal title="Add criterion" onClose={onClose} busy={saving}
      footer={<><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={save} disabled={saving || !name.trim()}>Add criterion</button></>}>
      <div className="form-row"><label className="form-label">Criterion *</label><input className="form-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="What must be true to pass?" /></div>
      <div className="form-row"><label className="form-label">Description</label><textarea className="form-textarea" rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} /></div>
      <div className="form-2col">
        <div className="form-row"><label className="form-label">Group</label>
          <select className="form-select" value={gid} onChange={(e) => setGid(e.target.value)}>
            <option value="">Ungrouped</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </div>
        <div className="form-row"><label className="form-label">Owner</label><OwnerSearch displayName={ownerName} onChange={(id, n) => { setOwnerId(id); setOwnerName(n); }} /></div>
      </div>
    </Modal>
  );
}

function MeetingModal({ gateId, profile, onClose, onSaved }) {
  const [type, setType] = useState('dry_run');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [chairId, setChairId] = useState(null);
  const [chairName, setChairName] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    await sb.from('gate_meetings').insert({ gate_id: gateId, meeting_type: type, title: title.trim() || null, scheduled_at: date ? new Date(date).toISOString() : null, chair_id: chairId || null, notes: notes.trim() || null, created_by: profile?.id || null });
    setSaving(false); onSaved();
  }
  return (
    <Modal title="Schedule meeting" onClose={onClose} busy={saving}
      footer={<><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={save} disabled={saving}>Schedule</button></>}>
      <div className="form-2col">
        <div className="form-row"><label className="form-label">Type</label>
          <select className="form-select" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="dry_run">Dry run</option><option value="formal">Formal review</option>
          </select>
        </div>
        <div className="form-row"><label className="form-label">Date</label><input className="form-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
      </div>
      <div className="form-row"><label className="form-label">Title</label><input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. G3 formal gate review" /></div>
      <div className="form-row"><label className="form-label">Chair</label><OwnerSearch displayName={chairName} onChange={(id, n) => { setChairId(id); setChairName(n); }} /></div>
      <div className="form-row"><label className="form-label">Notes</label><textarea className="form-textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
      <div style={{ fontSize: 11, color: '#888780' }}>Tip: the outcome is recorded separately after the meeting.</div>
    </Modal>
  );
}

function OutcomeModal({ gateId, meetingId, gate, gates, conditions, profile, onClose, onSaved }) {
  const [outcome, setOutcome] = useState('pass');
  const [rationale, setRationale] = useState('');
  const [condText, setCondText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const openConds = conditions.filter((c) => c.status === 'open');

  const NEW_STATUS = { pass: 'passed', conditional_pass: 'conditional_pass', fail: 'failed', defer: 'deferred' };

  async function save() {
    if (outcome === 'pass' && openConds.length > 0) { setError('Cannot Pass while open conditions exist. Use Conditional pass or close conditions first.'); return; }
    setSaving(true); setError(null);
    const { data: oRows, error: oErr } = await sb.from('gate_outcomes')
      .insert({ meeting_id: meetingId, gate_id: gateId, outcome, rationale: rationale.trim() || null, recorded_by: profile?.id || null })
      .select('id');
    if (oErr) { setSaving(false); setError(oErr.message); return; }
    const outcomeId = oRows?.[0]?.id;

    await sb.from('stage_gates').update({ status: NEW_STATUS[outcome], closed_date: new Date().toISOString().slice(0, 10) }).eq('id', gateId);

    if (outcome === 'conditional_pass' && condText.trim() && outcomeId) {
      const rows = condText.split('\n').map((l) => l.trim()).filter(Boolean).map((line) => {
        const [desc, , due] = line.split('·').map((s) => s.trim());
        return { outcome_id: outcomeId, gate_id: gateId, description: desc, due_date: due && /\d{4}-\d{2}-\d{2}/.test(due) ? due : null };
      });
      if (rows.length) await sb.from('gate_conditions').insert(rows);
    }

    if ((outcome === 'fail' || outcome === 'conditional_pass') && outcomeId && gate?.project_id) {
      await sb.from('replan_tasks').insert({ gate_id: gateId, outcome_id: outcomeId, project_id: gate.project_id, assigned_to: profile?.id || null });
    }
    setSaving(false);
    onSaved(outcome);
  }

  return (
    <Modal title="Record gate outcome" onClose={onClose} busy={saving}
      footer={<><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={save} disabled={saving}>Record outcome</button></>}>
      <div className="form-row"><label className="form-label">Outcome</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[['pass', 'Pass'], ['conditional_pass', 'Conditional pass'], ['fail', 'Fail'], ['defer', 'Defer']].map(([v, l]) => (
            <button key={v} type="button" className={`ftab${outcome === v ? ' active' : ''}`} onClick={() => setOutcome(v)}>{l}</button>
          ))}
        </div>
      </div>
      <div className="form-row"><label className="form-label">Rationale</label><textarea className="form-textarea" rows={3} value={rationale} onChange={(e) => setRationale(e.target.value)} placeholder="Decision rationale (recorded immutably)" /></div>
      {outcome === 'conditional_pass' && (
        <div className="form-row">
          <label className="form-label">Conditions (one per line: Condition · Owner · YYYY-MM-DD)</label>
          <textarea className="form-textarea" rows={3} value={condText} onChange={(e) => setCondText(e.target.value)} placeholder={'Complete UAT sign-off · Jane · 2026-06-15'} />
        </div>
      )}
      {error && <div className="form-error" style={{ margin: 0 }}>{error}</div>}
      <div style={{ fontSize: 11, color: '#888780' }}>
        The outcome updates gate status and is immutable once recorded.
        {shouldPropagateHandover(outcome) && findNextLifecycleGate(gates, gate) && (
          <> Handover outputs will copy to the next lifecycle gate as inherited inputs.</>
        )}
      </div>
    </Modal>
  );
}

function ConditionModal({ outcomeId, gateId, onClose, onSaved }) {
  const [desc, setDesc] = useState('');
  const [ownerId, setOwnerId] = useState(null);
  const [ownerName, setOwnerName] = useState('');
  const [due, setDue] = useState('');
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    await sb.from('gate_conditions').insert({ outcome_id: outcomeId, gate_id: gateId, description: desc.trim(), owner_id: ownerId || null, due_date: due || null });
    setSaving(false); onSaved();
  }
  return (
    <Modal title="Add condition" onClose={onClose} busy={saving}
      footer={<><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={save} disabled={saving || !desc.trim()}>Add condition</button></>}>
      <div className="form-row"><label className="form-label">Condition *</label><input className="form-input" value={desc} onChange={(e) => setDesc(e.target.value)} /></div>
      <div className="form-2col">
        <div className="form-row"><label className="form-label">Owner</label><OwnerSearch displayName={ownerName} onChange={(id, n) => { setOwnerId(id); setOwnerName(n); }} /></div>
        <div className="form-row"><label className="form-label">Due date</label><input className="form-input" type="date" value={due} onChange={(e) => setDue(e.target.value)} /></div>
      </div>
    </Modal>
  );
}

function DependencyPicker({ gate, gates, existing, profile, onClose, onSaved }) {
  const existingIds = existing.map((d) => d.depends_on_id);
  const options = gates.filter((g) => g.id !== gate.id && !existingIds.includes(g.id));
  const [saving, setSaving] = useState(false);
  async function add(depId) {
    setSaving(true);
    await sb.from('gate_dependencies').insert({ gate_id: gate.id, depends_on_id: depId, created_by: profile?.id || null });
    setSaving(false); onSaved();
  }
  return (
    <Modal title="Add dependency" onClose={onClose} footer={<button className="btn-ghost" onClick={onClose}>Close</button>}>
      {options.length === 0 ? <div className="empty">No other gates available to depend on.</div> : options.map((g) => (
        <div key={g.id} onClick={() => !saving && add(g.id)} style={{ padding: '9px 10px', borderRadius: 8, cursor: 'pointer', borderBottom: '0.5px solid #f1efe8' }}>
          <div style={{ fontSize: 12, color: '#2c2c2a', fontWeight: 500 }}>{g.name}</div>
          <div style={{ fontSize: 10, color: '#888780' }}>{GATE_TYPE_LABEL[g.gate_type]} · {STATUS_LABEL[g.status]}</div>
        </div>
      ))}
    </Modal>
  );
}

function RaidLinkModal({ gateId, criterionId, mode, programme, gateProjectId, existingLinks, profile, onClose, onSaved }) {
  // raise: create a new raid item then link; link: pick an existing programme RAID item
  const [type, setType] = useState('risk');
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (mode !== 'link') return;
    (async () => {
      const { data } = await sb.from('raid_items').select('id, type, title, status').eq('programme_id', programme.id).neq('status', 'closed').order('created_at', { ascending: false });
      const linkedIds = new Set(existingLinks.filter((l) => l.gate_id === gateId).map((l) => l.raid_item_id));
      setItems((data || []).filter((r) => !linkedIds.has(r.id)));
    })();
  }, [mode, gateId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function raise() {
    setSaving(true); setError(null);
    const { data, error: e } = await sb.from('raid_items')
      .insert({ programme_id: programme.id, project_id: gateProjectId || null, type, title: title.trim(), priority, status: 'open', owner_id: profile?.id || null })
      .select('id, title');
    if (e) { setSaving(false); setError(e.message); return; }
    await sb.from('gate_raid_links').insert({ gate_id: gateId, criterion_id: criterionId || null, raid_item_id: data[0].id, created_by: profile?.id || null });
    setSaving(false); onSaved(data[0].title);
  }

  async function link(item) {
    setSaving(true);
    await sb.from('gate_raid_links').insert({ gate_id: gateId, criterion_id: criterionId || null, raid_item_id: item.id, created_by: profile?.id || null });
    setSaving(false); onSaved(item.title);
  }

  if (mode === 'raise') {
    return (
      <Modal title="Raise RAID item" onClose={onClose} busy={saving}
        footer={<><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={raise} disabled={saving || !title.trim()}>Raise &amp; link</button></>}>
        <div className="form-2col">
          <div className="form-row"><label className="form-label">Type</label>
            <select className="form-select" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="risk">Risk</option><option value="issue">Issue</option><option value="dependency">Dependency</option><option value="decision">Decision</option>
            </select>
          </div>
          <div className="form-row"><label className="form-label">Priority</label>
            <select className="form-select" value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
            </select>
          </div>
        </div>
        <div className="form-row"><label className="form-label">Title *</label><input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Brief description" /></div>
        {error && <div className="form-error" style={{ margin: 0 }}>{error}</div>}
        <div style={{ fontSize: 11, color: '#888780' }}>This is added to the central RAID log and linked to this criterion.</div>
      </Modal>
    );
  }

  const filtered = items.filter((r) => r.title.toLowerCase().includes(q.toLowerCase()));
  return (
    <Modal title="Link existing RAID item" onClose={onClose} footer={<button className="btn-ghost" onClick={onClose}>Close</button>}>
      <input className="form-input" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div style={{ marginTop: 10, maxHeight: 320, overflowY: 'auto' }}>
        {items.length === 0 ? <div className="empty">No open RAID items available to link.</div> : filtered.map((r) => (
          <div key={r.id} onClick={() => !saving && link(r)} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '9px 10px', borderRadius: 8, cursor: 'pointer', borderBottom: '0.5px solid #f1efe8' }}>
            <TypeBadge type={r.type} short />
            <span style={{ fontSize: 12, color: '#2c2c2a' }}>{r.title}</span>
          </div>
        ))}
      </div>
    </Modal>
  );
}
